import { useAuthStore } from '../../stores/';

import styled from '@emotion/styled';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink } from 'react-router-dom';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { spacing, SpacingProps } from '@mui/system';
import { Box, Grid, Link, TextField as MuiTextField, Typography as MuiTypography } from '@mui/material';
import { LockPerson } from '@mui/icons-material';

import Center from '../../components/common/Center';
import GuestGuard from '../../components/guards/GuestGuard';
import MessageAlert from '../../components/common/MessageAlert';
import SubmitButton from '../../components/common/SubmitButton';

const TextField = styled(MuiTextField)<{ my?: number }>(spacing);

const Button = styled(SubmitButton)(spacing);

interface TypographyProps extends SpacingProps {
  as?: string;
}

const Typography = styled(MuiTypography)<TypographyProps>(spacing);

const LockIcon = styled(LockPerson)`
  width: 60px;
  height: 60px;
  margin: 0 auto ${(props) => props.theme.spacing(5)};
`;

function SignIn() {
  const isFirstLogin = !useAuthStore.use.hasExistingLogin();
  const email = useAuthStore.use.email();
  const signIn = useAuthStore.use.signIn();

  return (
    <>
      <GuestGuard>
        <Helmet title="Sign In" />
        <Center>
          <LockIcon color="primary" />
        </Center>
        <MuiTypography component="h1" variant="h3" align="center" gutterBottom>
          {isFirstLogin ? 'Welcome aboard' : 'Hello there'}!
        </MuiTypography>
        <MuiTypography component="h2" variant="subtitle1" align="center">
          Sign in to your account to continue
        </MuiTypography>
        <Box mt={5}>
          <Formik
            initialValues={{
              email: email ?? '',
              password: '',
              submit: false,
            }}
            validationSchema={Yup.object().shape({
              email: Yup.string().required('Email is required').email('Must be a valid email').max(255, 'Email max length is 255'),
              password: Yup.string().required('Password is required').max(255, 'Password max length is 255'),
            })}
            onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
              try {
                await signIn(values.email, values.password);
              } catch (error: any) {
                const message = error.message || 'Something went wrong';

                setStatus({ success: false });
                setErrors({ submit: message });
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
              <form noValidate onSubmit={handleSubmit}>
                <MessageAlert></MessageAlert>
                <TextField
                  autoComplete="email"
                  autoFocus
                  type="text"
                  name="email"
                  label="Email"
                  value={values.email}
                  inputProps={{
                    maxLength: 255,
                  }}
                  error={Boolean(touched.email && errors.email)}
                  fullWidth
                  helperText={touched.email && errors.email}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  my={2}
                />
                <TextField
                  autoComplete="current-password"
                  type="password"
                  name="password"
                  label="Password"
                  value={values.password}
                  inputProps={{
                    maxLength: 255,
                  }}
                  error={Boolean(touched.password && errors.password)}
                  fullWidth
                  helperText={touched.password && errors.password}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  my={2}
                />
                <Grid container mb={2} pl={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <Link to="../request-password-reset" component={RouterLink}>
                        Forgot password?
                      </Link>
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={6}
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Typography variant="body2">
                      <Link to="../request-invite" component={RouterLink}>
                        Request access
                      </Link>
                    </Typography>
                  </Grid>
                </Grid>
                <Button mt={3} text="Sign in" isSubmitting={isSubmitting}></Button>
              </form>
            )}
          </Formik>
        </Box>
      </GuestGuard>
    </>
  );
}

export default SignIn;
